import {
  ActiveRecord,
  BaseElementWidget,
  createRuntimeContextForWidget,
  CustomWidget,
  DslDefinition,
  FormWidget,
  queryOne,
  SPI,
  TableRowClickMode,
  TableWidget,
  TemplateDslDefinition,
  ViewType,
  Widget
} from '@kunlun/dependencies';
import TableWithTabs from './TableWithTabs.vue';

@SPI.ClassFactory(BaseElementWidget.Token({ viewType: ViewType.Table, widget: 'tableWidthTabs' }))
export class TableWidthTabs extends CustomWidget {
  @Widget.Reactive()
  protected tableWidgetDsl: DslDefinition | undefined;

  @Widget.Reactive()
  protected tableWidget: TableWidget | undefined;

  @Widget.Reactive()
  protected tableHeight = 400;

  @Widget.Reactive()
  protected formWidgetDsl: DslDefinition | undefined;

  @Widget.Reactive()
  protected formWidget: FormWidget | undefined;

  @Widget.Reactive()
  protected formLoading: boolean = false;

  @Widget.Reactive()
  protected formVisible: boolean = false;

  public initialize(props) {
    super.initialize(props);
    this.setComponent(TableWithTabs);
    this.tableWidgetDsl = this.getDsl().widgets.find((v) => (v as TemplateDslDefinition).slot === 'tableWidget');
    this.formWidgetDsl = this.getDsl().widgets.find((v) => (v as TemplateDslDefinition).slot === 'expandForm')?.widgets[0];
    this.initTableWidget();
    return this;
  }

  protected initTableWidget() {
    const dsl = this.tableWidgetDsl;
    if (!dsl) {
      console.error('Invalid table dsl.');
      return;
    }
    if (this.tableWidget) {
      this.tableWidget.dispose();
    }
    const runtimeContext = createRuntimeContextForWidget({
      type: ViewType.Table,
      model: this.model.model,
      modelName: this.model.name,
      template: dsl
    });
    const runtimeContextHandle = runtimeContext.handle;
    this.tableWidget = this.createWidget(CustomTableWidget, 'tableWidget', {
      metadataHandle: runtimeContextHandle,
      rootHandle: runtimeContextHandle,
      template: runtimeContext.viewTemplate
    });
  }

  @Widget.Method()
  @Widget.Provide()
  protected async initFormWidget(data: ActiveRecord) {
    const dsl = this.formWidgetDsl;
    if (!dsl) {
      console.error('Invalid table dsl.');
      return;
    }
    if (this.formWidget) {
      this.formWidget.dispose();
    }
    const model = this.model.model;
    const runtimeContext = createRuntimeContextForWidget({
      type: ViewType.Form,
      model,
      modelName: this.model.name,
      template: dsl
    });
    const runtimeContextHandle = runtimeContext.handle;
    this.formLoading = true;
    try {
      const dataSource = await queryOne(model, { id: data.id as string }, runtimeContext.getRequestFields());
      this.formWidget = this.createWidget(FormWidget, 'expandForm', {
        metadataHandle: runtimeContextHandle,
        rootHandle: runtimeContextHandle,
        dataSource: dataSource,
        activeRecords: dataSource,
        template: runtimeContext.viewTemplate,
        inline: true
      });
      this.formVisible = true;
    } finally {
      this.formLoading = false;
    }
  }
}

export class CustomTableWidget extends TableWidget {
  @Widget.Method()
  @Widget.Inject()
  protected initFormWidget: ((data: ActiveRecord) => void) | undefined;

  @Widget.Reactive()
  protected get allowRowClick(): boolean {
    return true;
  }

  @Widget.Reactive()
  protected get rowClickMode(): TableRowClickMode[] | undefined {
    return [TableRowClickMode.dblclick];
  }

  @Widget.Method()
  protected async onRowDblClick({ column, row }: { column: any; row: any }): Promise<void> {
    if (!column?.field) {
      return;
    }
    this.initFormWidget?.(row);
  }
}
